<section class="py-6 bg-slate-50 relative overflow-hidden">
  <div class="max-w-6xl mx-auto px-4 sm:px-6 relative z-10">

    <!-- Header Section -->
    <div class="text-center mb-16">
      <div class="text-xs font-bold text-blue-600 tracking-widest uppercase mb-4">The Difference</div>
      <h2 class="text-3xl md:text-5xl font-extrabold text-slate-900 mb-6 tracking-tight">
        Why choose <span class="text-indigo-600">BeExpert.ai</span>
      </h2>
      <p class="text-lg text-slate-500 max-w-3xl mx-auto">
        See how an AI-first hiring platform outperforms the traditional recruitment process across every stage.
      </p>
    </div>

    <!-- Comparison Table -->
    <div class="bg-white rounded-3xl shadow-xl border border-slate-100 overflow-hidden">
      <div class="overflow-x-auto">
        <table class="w-full min-w-[800px] border-collapse">
          <thead>
            <tr>
              <!-- Criteria Header -->
              <th
                class="bg-[#0b1120] text-slate-500 text-[11px] font-extrabold uppercase tracking-[0.2em] text-left py-6 px-6 sm:px-10 w-[20%] rounded-tl-2xl border-r border-slate-800/50">
                CRITERIA
              </th>

              <!-- BeExpert Header -->
              <th
                class="bg-gradient-to-r from-blue-600 via-indigo-500 to-indigo-600 text-left py-6 px-6 sm:px-8 w-[35%] border-r border-indigo-700/30 relative">
                <div class="flex items-center justify-between">
                  <div class="flex items-start gap-3">
                    <div
                      class="w-9 h-9 rounded-[10px] bg-white/20 flex items-center justify-center flex-shrink-0 mt-0.5 shadow-sm border border-white/10">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="white"
                        stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-5 h-5">
                        <path
                          d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z" />
                        <path d="M5 3v4" />
                        <path d="M19 17v4" />
                        <path d="M3 5h4" />
                        <path d="M17 19h4" />
                      </svg>
                    </div>
                    <div class="flex flex-col">
                      <span class="text-white font-bold text-[17px] leading-tight tracking-wide">BeExpert.ai</span>
                      <span class="text-indigo-100/90 text-xs font-medium mt-0.5">AI-powered hiring</span>
                    </div>
                  </div>
                  <div
                    class="hidden lg:flex bg-[#ffc107] text-[#452703] text-[10px] font-black px-2.5 py-1.5 rounded-full items-center gap-1.5 uppercase tracking-wider shadow-sm ml-4">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" class="w-3 h-3">
                      <path d="M12 2L14.5 9.5L22 12L14.5 14.5L12 22L9.5 14.5L2 12L9.5 9.5L12 2Z"></path>
                    </svg>
                    RECOMMENDED
                  </div>
                </div>
              </th>

              <!-- Agency Header -->
              <th class="bg-[#0b1120] text-left py-6 px-6 sm:px-8 w-[22.5%] border-r border-slate-800/50">
                <div class="flex items-start gap-3">
                  <div
                    class="w-9 h-9 rounded-[10px] bg-[#1e293b] flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg viewBox="0 0 24 24" fill="none" stroke="#94a3b8" stroke-width="2" class="w-4 h-4">
                      <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
                      <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
                    </svg>
                  </div>
                  <div class="flex flex-col">
                    <span class="text-white font-bold text-[16px] leading-tight tracking-wide">Agency Hiring</span>
                    <span class="text-slate-400 text-xs font-medium mt-0.5">Third-party recruiters</span>
                  </div>
                </div>
              </th>

              <!-- Portals Header -->
              <th class="bg-[#0b1120] text-left py-6 px-6 sm:px-8 w-[22.5%] rounded-tr-2xl">
                <div class="flex items-start gap-3">
                  <div
                    class="w-9 h-9 rounded-[10px] bg-[#1e293b] flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg viewBox="0 0 24 24" fill="none" stroke="#94a3b8" stroke-width="2" class="w-4 h-4">
                      <circle cx="12" cy="12" r="10"></circle>
                      <line x1="2" y1="12" x2="22" y2="12"></line>
                      <path
                        d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z">
                      </path>
                    </svg>
                  </div>
                  <div class="flex flex-col">
                    <span class="text-white font-bold text-[16px] leading-tight tracking-wide">Job Portals</span>
                    <span class="text-slate-400 text-xs font-medium mt-0.5">Traditional job boards</span>
                  </div>
                </div>
              </th>
            </tr>
          </thead>

          <tbody class="divide-y divide-slate-100 relative">
            @for (row of comparisonRows; track row.criteria) {
            <tr
              class="hover:bg-white hover:shadow-[0_4px_20px_-4px_rgba(0,0,0,0.08)] relative z-0 hover:z-10 transition-all duration-300 bg-white">

              <!-- Criteria Column -->
              <td class="py-2.5 px-6 align-middle">
                <div class="flex items-center gap-4">
                  @if (!row.isBottomLine) {
                  <div
                    class="w-10 h-10 rounded-[10px] bg-[#f0f3ff] text-indigo-600 flex items-center justify-center flex-shrink-0 shadow-sm border border-indigo-100/50">
                    @if (row.icon === 'scan') {
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-5 h-5">
                      <path d="M3 7V5a2 2 0 0 1 2-2h2"></path>
                      <path d="M17 3h2a2 2 0 0 1 2 2v2"></path>
                      <path d="M21 17v2a2 2 0 0 1-2 2h-2"></path>
                      <path d="M7 21H5a2 2 0 0 1-2-2v-2"></path>
                      <circle cx="12" cy="12" r="3"></circle>
                    </svg>
                    } @else if (row.icon === 'badge') {
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-5 h-5">
                      <path d="M12 15v5s3-1.5 5-5V7a2 2 0 0 0-2-2H9a2 2 0 0 0-2 2v8c2 3.5 5 5 5 5z"></path>
                      <circle cx="12" cy="11" r="2"></circle>
                    </svg>
                    } @else if (row.icon === 'zap') {
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-5 h-5">
                      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon>
                    </svg>
                    } @else if (row.icon === 'shield') {
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-5 h-5">
                      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                      <path d="M9 12l2 2 4-4"></path>
                    </svg>
                    } @else if (row.icon === 'file-text') {
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-5 h-5">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                      <polyline points="14 2 14 8 20 8"></polyline>
                      <line x1="16" y1="13" x2="8" y2="13"></line>
                      <line x1="16" y1="17" x2="8" y2="17"></line>
                      <polyline points="10 9 9 9 8 9"></polyline>
                    </svg>
                    } @else if (row.icon === 'trending-up') {
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-5 h-5">
                      <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"></polyline>
                      <polyline points="17 6 23 6 23 12"></polyline>
                    </svg>
                    } @else if (row.icon === 'target') {
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-5 h-5">
                      <circle cx="12" cy="12" r="10"></circle>
                      <circle cx="12" cy="12" r="6"></circle>
                      <circle cx="12" cy="12" r="2"></circle>
                    </svg>
                    } @else if (row.icon === 'users') {
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-5 h-5">
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                      <circle cx="9" cy="7" r="4"></circle>
                      <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                      <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                    </svg>
                    } @else if (row.icon === 'bar-chart') {
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-5 h-5">
                      <line x1="18" y1="20" x2="18" y2="10"></line>
                      <line x1="12" y1="20" x2="12" y2="4"></line>
                      <line x1="6" y1="20" x2="6" y2="14"></line>
                    </svg>
                    } @else if (row.icon === 'layers') {
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-5 h-5">
                      <polygon points="12 2 2 7 12 12 22 7 12 2"></polygon>
                      <polyline points="2 12 12 17 22 12"></polyline>
                      <polyline points="2 17 12 22 22 17"></polyline>
                    </svg>
                    }
                  </div>
                  }
                  <span class="text-sm font-bold text-slate-800" [class.ml-5]="row.isBottomLine">{{ row.criteria
                    }}</span>
                </div>
              </td>

              <!-- BeExpert Column -->
              <td class="py-2.5 px-6 align-middle border-x border-slate-100 bg-white">
                <div class="flex items-center gap-3">
                  <svg viewBox="0 0 24 24" class="w-5 h-5 text-emerald-500 flex-shrink-0 drop-shadow-sm"
                    fill="currentColor">
                    <path fill-rule="evenodd"
                      d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm13.36-1.814a.75.75 0 10-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.14-.094l3.75-5.25z"
                      clip-rule="evenodd" />
                  </svg>
                  <span class="text-[13px] text-slate-800 font-medium">
                    {{ row.beExpert }}
                  </span>
                </div>
              </td>

              <!-- Agency Column -->
              <td class="py-2.5 px-6 align-middle bg-slate-50/50 border-r border-slate-100">
                <span class="text-sm text-slate-500 font-medium">
                  {{ row.agency }}
                </span>
              </td>

              <!-- Portals Column -->
              <td class="py-2.5 px-6 align-middle bg-slate-50/50 rounded-r-3xl">
                <span class="text-sm text-slate-500 font-medium">
                  {{ row.portals }}
                </span>
              </td>

            </tr>
            }
          </tbody>
        </table>
      </div>
    </div>

  </div>
</section>